import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  isSameDay,
} from "date-fns";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { utils, writeFile } from "xlsx";
import Swal from "sweetalert2";
import { API_BASE, API_HOST } from "config/api";

export default function useMemberData() {
  const { church } = useAuth();
  const [memberData, setMemberData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%" },
      { Header: "Member ID", accessor: "memberId", align: "center", width: "10%" },
      { Header: "Member", accessor: "memberName", align: "left", width: "25%" },
      { Header: "Phone", accessor: "phone", align: "left", width: "15%" },
      { Header: "Email", accessor: "email", align: "left", width: "15%" },
      { Header: "Department", accessor: "department", align: "center", width: "10%" },
      { Header: "Ministry", accessor: "ministry", align: "center", width: "10%" },
      { Header: "Date of Birth", accessor: "dob", align: "center", width: "10%" },
      { Header: "Place of Birth", accessor: "placeOfBirth", align: "center", width: "10%" },
      { Header: "Nationality", accessor: "nationality", align: "center", width: "10%" },
      { Header: "Home Town", accessor: "homeTown", align: "center", width: "10%" },
      { Header: "Region", accessor: "region", align: "center", width: "10%" },
      { Header: "Gender", accessor: "gender", align: "center", width: "10%" },
      {
        Header: "Residential Address",
        accessor: "residentialAddress",
        align: "center",
        width: "10%",
      },
      { Header: "Languages", accessor: "languages", align: "center", width: "10%" },
      { Header: "Profession", accessor: "profession", align: "center", width: "10%" },
      { Header: "Rank", accessor: "rank", align: "center", width: "10%" },
      { Header: "Employment", accessor: "employment", align: "center", width: "10%" },
      { Header: "Name of Employment", accessor: "nameOfEmployment", align: "center", width: "10%" },
      { Header: "Education", accessor: "education", align: "center", width: "10%" },
      { Header: "Marital Status", accessor: "maritalStatus", align: "center", width: "10%" },
      { Header: "Spouse Name", accessor: "spouseName", align: "center", width: "10%" },
      { Header: "Date of Marriage", accessor: "dateOfMarraige", align: "center", width: "10%" },
      { Header: "By Pastor", accessor: "byPastor", align: "center", width: "10%" },
      { Header: "Number of children", accessor: "numberOfChildren", align: "center", width: "10%" },
      { Header: "Number of Male", accessor: "numberOfMale", align: "center", width: "10%" },
      { Header: "Number of Female", accessor: "numberOfFemale", align: "center", width: "10%" },
      { Header: "Name of Parent", accessor: "nameOfParent", align: "center", width: "10%" },
      { Header: "Next of Kin", accessor: "nextOfKin", align: "center", width: "10%" },
      { Header: "Year of Conversion", accessor: "yearOfConversion", align: "center", width: "10%" },
      { Header: "Are you Baptized", accessor: "areYouBaptized", align: "center", width: "10%" },
      { Header: "Baptism Type", accessor: "baptismType", align: "center", width: "10%" },
      { Header: "Baptism Date", accessor: "baptismDate", align: "center", width: "10%" },
      { Header: "Baptism Church", accessor: "baptismChurch", align: "center", width: "10%" },
      { Header: "Baptism By", accessor: "baptismBy", align: "center", width: "10%" },
      {
        Header: "Baptism Certificate",
        accessor: "baptismCertificate",
        align: "center",
        width: "10%",
      },
      { Header: "Previous Church", accessor: "previousChurch", align: "center", width: "10%" },
      {
        Header: "Previous Church Address",
        accessor: "previousChurchAddress",
        align: "center",
        width: "10%",
      },
      { Header: "Place of Interest", accessor: "placeOfInterest", align: "center", width: "10%" },
      { Header: "Will You Pay Tithe", accessor: "willYouPayType", align: "center", width: "10%" },
      { Header: "Communicant", accessor: "communicant", align: "center", width: "10%" },
      {
        Header: "Will you Pay Welfare",
        accessor: "willYouPayWelfare",
        align: "center",
        width: "10%",
      },
      { Header: "Membership Date", accessor: "membershiptDate", align: "center", width: "10%" },
      { Header: "Role", accessor: "role", align: "center", width: "10%" },
      { Header: "Photo", accessor: "photo", align: "center", width: "10%" },
    ],
    rows: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("all");
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [totalMembers, setTotalMembers] = useState(0);

  const fetchMemberData = async (startDate, endDate) => {
    try {
      setIsLoading(true);
      setError(null);

      if (!church?.id) {
        throw new Error("Church context not available");
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.append("start_date", format(startDate, "yyyy-MM-dd"));
        params.append("end_date", format(endDate, "yyyy-MM-dd"));
      }

      const response = await fetch(`${API_BASE}/members/report/${church.id}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch members");
      }

      const result = await response.json();
      const members = result.data || [];

      const rows = members.map((member, index) => ({
        id: member.id,
        serial: index + 1,
        memberId: member.memberID || "N/A",
        memberName: `${member.first_name || ""} ${member.surname || ""}`.trim(),
        phone: member.phone || "N/A",
        email: member.email || "N/A",
        department: member.department?.name || "N/A",
        ministry: member.ministry?.name ?? "N/A",
        dob: member.date_of_birth ? format(new Date(member.date_of_birth), "MMM d, yyyy") : "N/A",
        placeOfBirth: member.place_of_birth || "N/A",
        nationality: member.nationality || "N/A",
        homeTown: member.home_town || "N/A",
        region: member.region || "N/A",
        gender: member.gender || "N/A",
        residentialAddress: member.residential_address || "N/A",
        languages: member.languages || "N/A",
        profession: member.profession || "N/A",
        rank: member.rank || "N/A",
        employment: member.employment || "N/A",
        nameOfEmployment: member.name_of_employment || "N/A",
        education: member.education || "N/A",
        maritalStatus: member.marital_status || "N/A",
        spouseName: member.spouse_name || "N/A",
        dateOfMarraige: member.date_of_marriage
          ? format(new Date(member.date_of_marriage), "MMM d, yyyy")
          : "N/A",
        byPastor: member.by_pastor || "N/A",
        numberOfChildren: member.number_of_children || "N/A",
        numberOfMale: member.number_of_male || "N/A",
        numberOfFemale: member.number_of_female || "N/A",
        nameOfParent: member.name_of_parent || "N/A",
        nextOfKin: member.next_of_kin || "N/A",
        yearOfConversion: member.year_of_conversion || "N/A",
        areYouBaptized: member.are_you_baptized ? "Yes" : "No",
        baptismType: member.baptism_type || "N/A",
        baptismDate: member.baptism_date
          ? format(new Date(member.baptism_date), "MMM d, yyyy")
          : "N/A",
        baptismChurch: member.baptism_church || "N/A",
        baptismBy: member.baptism_by || "N/A",
        baptismCertificate: member.baptism_certificate ? "Yes" : "No",
        previousChurch: member.previous_church || "N/A",
        previousChurchAddress: member.previous_church_address || "N/A",
        placeOfInterest: member.place_of_interest || "N/A",
        willYouPayType: member.will_you_pay_tithe ? "Yes" : "No",
        communicant: member.communicant ? "Yes" : "No",
        willYouPayWelfare: member.will_you_pay_welfare ? "Yes" : "No",
        membershiptDate: member.membership_date
          ? format(new Date(member.membership_date), "MMM d, yyyy")
          : "N/A",
        role: member.role || "N/A",
        photo: member.photo || "N/A",
        originalData: member,
      }));

      setMemberData((prev) => ({ ...prev, rows }));
    } catch (error) {
      console.error("Failed to fetch members:", error);
      setError(error.message);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to load members",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    fetchMemberData(newPeriod);
  };

  const handleCustomDateChange = (start, end) => {
    setPeriod("custom");
    fetchMemberData("custom", start, end);
  };

  const exportToCSV = () => {
    const headers = memberData.columns.map((col) => col.Header);
    const data = memberData.rows.map((row) => {
      return memberData.columns.map((col) => row[col.accessor] || "");
    });

    const csvContent = [headers.join(","), ...data.map((row) => row.join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `members_report_${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const title = `Members Report (${format(new Date(), "MMM d, yyyy")})`;

    doc.setFontSize(16);
    doc.text(title, 14, 15);

    const headers = memberData.columns.map((col) => col.Header);
    const data = memberData.rows.map((row) => {
      return memberData.columns.map((col) => row[col.accessor] || "");
    });

    doc.autoTable({
      head: [headers],
      body: data,
      startY: 25,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
    });

    doc.save(`members_report_${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  const printReport = () => {
    const printWindow = window.open("", "_blank");
    const title = `Members Report (${format(new Date(), "MMM d, yyyy")})`;

    const htmlContent = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background-color: #2980b9; color: white; text-align: left; padding: 8px; }
            td { padding: 8px; border-bottom: 1px solid #ddd; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            @media print {
              body { margin: 0; padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <button class="no-print" onclick="window.print()">Print</button>
          <button class="no-print" onclick="window.close()">Close</button>
          <table>
            <thead>
              <tr>
                ${memberData.columns.map((col) => `<th>${col.Header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${memberData.rows
                .map(
                  (row) => `
                <tr>
                  ${memberData.columns.map((col) => `<td>${row[col.accessor] || ""}</td>`).join("")}
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          <p>Generated on ${format(new Date(), "MMM d, yyyy h:mm a")}</p>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleExport = (type) => {
    switch (type) {
      case "csv":
        exportToCSV();
        break;
      case "pdf":
        exportToPDF();
        break;
      case "print":
        printReport();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (church?.id) {
      fetchMemberData();
    }
  }, [church?.id]);

  return {
    columns: memberData.columns,
    rows: memberData.rows,
    isLoading,
    error,
    period,
    dateRange,
    handlePeriodChange,
    handleCustomDateChange,
    handleExport,
    totalMembers,
    fetchMemberData, // Expose fetchMemberData for external use
  };
}
